<template>
  <div
    v-if="appointment"
    :class="[
      'w-full h-full overflow-hidden timeline-event',
      `timeline-event-${originalDetails?.id}`,
      ...eventStyles.classes,
      'hover:ring-[var(--status-color)]'
    ]"
    :style="{
      background: eventStyles.colors.background,
      color: eventStyles.colors.text,
      borderColor: `${eventStyles.colors.status} !important`,
      '--status-color': eventStyles.colors.status
    }"
  >
    <div>
      <div class="flex items-center gap-2 justify-between">
        <div
          class="flex items-center gap-2 whitespace-nowrap no-hover truncate"
        >
          <div
            v-if="appointment.status === 'accepted' && appointment.late"
            class="flex items-center gap-1 text-red-500"
          >
            <WarningIcon class="h-3" />Late
          </div>
          <div v-else>
            <div
              v-if="originalDetails?.is_ghost"
              class="text-white font-semibold text-xxs rounded-lg px-1"
              :style="{ background: eventStyles.colors.status }"
            >
              {{ statusLabel }}
            </div>
            <span
              v-else
              class="flex items-center text-xs font-medium text-gray-900 dark:text-white gap-1"
            >
              <div
                class="flex w-2.5 h-2.5 rounded-full"
                :style="{ background: eventStyles.colors.status }"
              ></div>
              <div :style="{ color: eventStyles.colors.status }">
                {{ statusLabel }}
              </div>
            </span>
          </div>
          {{ timeRangeLabel }}
        </div>
        <div v-if="appointment.status === 'pending'" class="on-hover truncate">
          <span :style="{ color: eventStyles.colors.text }">
            {{ estimatedDurationLabel }}
          </span>
        </div>
        <AppointmentEventProgress
          v-else
          class="on-hover"
          :appointment="appointment"
          :label="statusLabel"
        />
        <div class="flex gap-1 no-hover">
          <UserCheck
            v-if="originalDetails?.created_by?.user_type === 'customer'"
            :class="['h-3']"
            :style="{ color: eventStyles.colors.status }"
          />
          <Lock
            v-if="appointment.assignment_pinned"
            :class="['h-3']"
            :style="{ color: eventStyles.colors.status }"
          />
          <CrownSimple
            v-if="appointment.customer.membership"
            :class="['h-3']"
            :style="{ color: eventStyles.colors.status }"
          />
        </div>
      </div>
      <div class="flex items-center justify-between">
        <div class="truncate">
          <EventInfoTooltip
            :event="event"
            :appointment="appointment"
            :icon-color="eventStyles.colors.text"
          />
        </div>
      </div>
    </div>
  </div>
  <div
    v-else
    :class="[
      'w-full h-full overflow-hidden timeline-event',
      'timeline-event-non-appointment',
      `timeline-event-${originalDetails?.event_details?.id}`,
      ...eventStyles.classes,
      'hover:ring-[var(--status-color)]'
    ]"
    :style="{
      background: eventStyles.colors.background,
      color: eventStyles.colors.text,
      borderColor: `${eventStyles.colors.text} !important`,
      '--status-color': eventStyles.colors.text
    }"
    :data-tippy-content="event.title"
  >
    <div>
      <div class="flex items-center gap-2 justify-between">
        <div class="flex items-center gap-1 whitespace-nowrap no-hover">
          <div
            class="flex items-center justify-center text-[8px] font-semibold text-white bg-gray-400 px-1 rounded-full h-4"
          >
            EVENT
          </div>
          <div>
            {{
              originalDetails?.event_details?.exclusive
                ? "Unavailable"
                : "Available"
            }}
          </div>
        </div>
        <div class="flex gap-1 items-center on-hover whitespace-nowrap">
          <EmptyIcon
            v-if="originalDetails?.event_details?.exclusive"
            class="h-4 text-gray-400"
          />
          <div v-else class="bg-gray-400 h-2 w-2 rounded-full"></div>
          {{ timeRangeLabel }}
        </div>
      </div>
      <div class="flex items-center justify-between">
        <div class="font-semibold truncate">
          {{ event.title }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import moment from "moment-timezone";
import { computed } from "vue";
import { customDateFormatWithTimezone } from "@/lib/helpers";

import CrownSimple from "@/images/CrownSimple.svg?component";
import EmptyIcon from "@/images/Empty.svg?component";
import Lock from "@/images/lock.svg?component";
import UserCheck from "@/images/UserCheck.svg?component";
import WarningIcon from "@/images/Warning.svg?component";
import { formatDuration } from "@/mixins";
import { formatTimeDuration } from "@/mixins";
import { useScheduleColors } from "@/SchedulerV2/useScheduleColors";
import { AppointmentEvent, EventAPIResponse } from "@/typings/scheduler";
import AppointmentEventProgress from "./AppointmentEventProgress.vue";
import EventInfoTooltip from "./EventInfoTooltip.vue";

const { getEventStyles } = useScheduleColors();
const props = defineProps<{
  event: AppointmentEvent;
}>();
const originalDetails = computed<EventAPIResponse | undefined>(
  () => props.event.original?.details
);
const appointment = computed(() => originalDetails.value?.appointment);
const technician = computed(() => {
  if (appointment.value) {
    return originalDetails.value?.technician;
  }
  const tech = originalDetails.value?.event_details?.technicians?.find(
    (tech) => tech.id === props.event.resource
  );
  return tech;
});
const eventStyles = computed(() => {
  return getEventStyles(
    appointment.value,
    Boolean(originalDetails.value?.is_ghost) || appointment.value?.ghost
  );
});
const statusLabel = computed(() => {
  if (originalDetails.value?.is_ghost) {
    return `Hold ${customDateFormatWithTimezone(
      appointment.value?.arrival_window_start_time,
      "h:mm"
    )}`;
  }
  switch (appointment.value?.status) {
    case "accepted": {
      if (appointment.value.travel_start_time) {
        return appointment.value.late ? "Late" : "Accepted";
      }
      return "Claimed";
    }
    case "running":
      return "Working";
    case "completed":
      return appointment.value.job_invoice_revenue_cents > 0
        ? "Completed"
        : "Completed (No Revenue)";
    default:
      return "Pending";
  }
});
const estimatedDurationLabel = computed(() => {
  const duration = moment.duration(
    appointment.value?.estimated_duration_minutes,
    "minutes"
  );
  return `Estimated Duration ~${formatTimeDuration(duration)}`;
});
const timeRangeLabel = computed(() => {
  if (!appointment.value) {
    const inputFormat = props.event.start.indexOf("M") > -1 ? "h:mm A" : "";
    return `${customDateFormatWithTimezone(
      props.event.start,
      "h:mmA",
      window.currentTenant.timezone,
      inputFormat
    )} - ${customDateFormatWithTimezone(
      props.event.end,
      "h:mmA",
      window.currentTenant.timezone,
      inputFormat
    )}`;
  }
  if (!technician.value) {
    return `${customDateFormatWithTimezone(
      appointment.value?.arrival_window_start_time,
      "h:mmA"
    )} - ${customDateFormatWithTimezone(
      appointment.value?.arrival_window_end_time,
      "h:mmA"
    )}`;
  }
  switch (appointment.value?.status) {
    case "running":
      return `${customDateFormatWithTimezone(
        props.event.start,
        "h:mmA",
        window.currentTenant.timezone,
        "h:mmA"
      )} - ${customDateFormatWithTimezone(
        props.event.end,
        "h:mmA",
        window.currentTenant.timezone,
        "h:mmA"
      )}`;
    case "completed":
      return "";
    default:
      return `${customDateFormatWithTimezone(
        appointment.value?.arrival_window_start_time,
        "h:mmA"
      )} - ${customDateFormatWithTimezone(
        appointment.value?.arrival_window_end_time,
        "h:mmA"
      )}`;
  }
});
</script>

<style lang="scss" scoped>
/*
  Shop, Lunch and the other calendar events, hatched on the phone board so a
  column reads as blocked rather than booked -- at a column's width there is no
  room to say so in words.

  `!important` because the element above sets `background` inline, and the
  shorthand would otherwise clear the image. Only the image is overridden, so
  each event keeps the background COLOUR its status gives it.
*/
.phone-columns .timeline-event-non-appointment {
  background-image: repeating-linear-gradient(
    45deg,
    transparent 0 4px,
    rgb(0 0 0 / 8%) 4px 8px
  ) !important;
}

.timeline-event {
  .on-hover {
    display: none !important;
  }
  .no-hover {
    display: flex !important;
  }
}
.timeline-event-hovered {
  .on-hover {
    display: flex !important;
  }
  .no-hover {
    display: none !important;
  }
}
</style>
